// Source: https://usehooks-ts.com/react-hook/use-resize-observer.
import { useEffect, useRef, useState } from 'react';
import useIsMounted from './use-is-mounted.js';
function extractSize(entry, box, sizeType) {
    if (!entry[box]) {
        if (box === 'contentBoxSize') {
            return entry.contentRect[sizeType === 'inlineSize' ? 'width' : 'height'];
        }
        return undefined;
    }
    return Array.isArray(entry[box])
        ? entry[box][0][sizeType]
        : // Support Firefox's non-standard behavior.
            entry[box][sizeType];
}
const initialSize = {
    width: undefined,
    height: undefined,
};
/**
 * Custom hook that observes the size of an element using the ResizeObserver API.
 */
export function useResizeObserver(options) {
    const { ref, box = 'content-box' } = options;
    const [size, setSize] = useState(initialSize);
    const { width, height } = size;
    const previousSize = useRef({ ...initialSize });
    const onResize = useRef(undefined);
    onResize.current = options.onResize;
    const isMounted = useIsMounted();
    useEffect(() => {
        if (!ref.current) {
            return;
        }
        if (typeof window === 'undefined' || !('ResizeObserver' in window)) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            const boxProperty = box === 'border-box'
                ? 'borderBoxSize'
                : box === 'device-pixel-content-box'
                    ? 'devicePixelContentBoxSize'
                    : 'contentBoxSize';
            const newWidth = extractSize(entry, boxProperty, 'inlineSize');
            const newHeight = extractSize(entry, boxProperty, 'blockSize');
            const hasChanged = previousSize.current.width !== newWidth ||
                previousSize.current.height !== newHeight;
            if (hasChanged) {
                const newSize = { width: newWidth, height: newHeight };
                previousSize.current.width = newWidth;
                previousSize.current.height = newHeight;
                if (onResize.current) {
                    onResize.current(newSize);
                }
                else {
                    if (isMounted()) {
                        setSize(newSize);
                    }
                }
            }
        });
        observer.observe(ref.current, { box });
        return () => {
            observer.disconnect();
        };
    }, [box, ref, isMounted]);
    return { width, height };
}
export default useResizeObserver;
